const envValue = process.env.REACT_APP_USE_WEBCRYPTO;
const useWebCrypto  = (envValue === undefined || envValue === 'undefined' || envValue === "true" || envValue === true  );

// The API can intentionally return plain JSON when WebCrypto encryption is
// disabled, and a few legacy endpoints may still return a plain JSON object.
// Never send those objects to atob(): it turns a harmless response into an
// InvalidCharacterError before the caller can handle the API result.
export const isEncryptedElakayPayload = (payload) => Boolean(
  payload && typeof payload === "object" &&
  ["iv", "salt", "ciphertext", "tag"].every((key) => typeof payload[key] === "string" && payload[key].length > 0)
);

// PBKDF2 (100 000 rounds) is the costly part of each decryption. The server
// reuses one salt for a while (server_elakay cryptageElakay.js), so the derived
// key is kept per salt; a response with a new salt simply derives a new key.
const AES_KEY_CACHE_MAX = 8;
const aesKeyCache = new Map();
let passwordKeyPromise;
const deriveAesKey = (saltB64, saltBytes) => {
  const cached = aesKeyCache.get(saltB64);
  if (cached) return cached;
  if (!passwordKeyPromise) {
    passwordKeyPromise = crypto.subtle.importKey(
      "raw",
      new TextEncoder().encode(`${process.env.REACT_APP_DECRYPTO_ENCRYPTO_ELAKAY}`),
      { name: "PBKDF2" },
      false,
      ["deriveKey"]
    ).catch((error) => { passwordKeyPromise = undefined; throw error; });
  }
  const aesKey = passwordKeyPromise.then((passwordKey) => crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: saltBytes, iterations: 100000, hash: "SHA-256" },
    passwordKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"]
  )).catch((error) => {
    if (aesKeyCache.get(saltB64) === aesKey) aesKeyCache.delete(saltB64);
    throw error;
  });
  aesKeyCache.set(saltB64, aesKey);
  if (aesKeyCache.size > AES_KEY_CACHE_MAX) aesKeyCache.delete(aesKeyCache.keys().next().value);
  return aesKey;
};

export const decryptData = async (encryptedData) => {
  if (!useWebCrypto || !isEncryptedElakayPayload(encryptedData)) return encryptedData;

  // Helper pour décoder du Base64 vers un Uint8Array. Validate the field
  // before calling atob so malformed API payloads become a useful Error.
  const decodeBase64 = (b64, field) => {
    try {
      return Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    } catch (_error) {
      throw new Error(`Réponse chiffrée invalide (${field}).`);
    }
  };

  // 1-2. Décoder les champs Base64 en tableaux d'octets
  const ivBytes = decodeBase64(encryptedData.iv, "iv");
  const saltBytes = decodeBase64(encryptedData.salt, "salt");
  const ciphertextBytes = decodeBase64(encryptedData.ciphertext, "ciphertext");
  const tagBytes = decodeBase64(encryptedData.tag, "tag");

  // 3. Clé AES-256-GCM dérivée via PBKDF2 (paramètres identiques au serveur), en cache par sel
  const aesKey = await deriveAesKey(encryptedData.salt, saltBytes);

  // 4. Combiner ciphertext + tag en un seul buffer
  const encryptedContent = new Uint8Array(ciphertextBytes?.length + tagBytes?.length);
  encryptedContent.set(ciphertextBytes, 0);
  encryptedContent.set(tagBytes, ciphertextBytes?.length);

  // 5. Déchiffrer avec AES-GCM
  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: ivBytes, tagLength: 128 },
    aesKey,
    encryptedContent
  );

  // 6. Convertir le résultat binaire en texte puis en JSON
  const decryptedText = new TextDecoder().decode(decryptedBuffer);
  return JSON.parse(decryptedText);
};
